// The set picker: every practice set of a task type by name, numbered, with
// the student's completion state and best score. Choosing beats being served.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Circle } from 'lucide-react';

import { api } from '@/lib/api';
import { prettyTask } from '@/lib/labels';
import AppShell from '@/components/AppShell';
import { cn } from '@/lib/utils';

export default function SetPickerPage() {
  const { exam, taskType } = useParams();
  const [sets, setSets] = useState(null);
  const [units, setUnits] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [s, c] = await Promise.all([api.practiceSetList(exam, taskType), api.curriculum(exam)]);
        setSets(s.data.sets);
        setUnits(c.data.units);
      } catch (err) {
        setError(err.response?.data?.detail || 'No sets for this task type yet.');
      }
    };
    load();
  }, [exam, taskType]);

  const unit = useMemo(() => units.find((u) => u.task_type === taskType), [units, taskType]);
  const section = unit?.section;

  const setPath = (s) => {
    // LLM-graded, terminal submission on file -> route straight to the
    // read-only review. Writing + speaking are one-shot per set: no re-runner.
    if (s.locked && s.review_submission_id) {
      return `/${exam}/submissions/${s.review_submission_id}/review`;
    }
    const base = section === 'writing' && taskType !== 'l_sst' ? 'write' : section === 'speaking' ? 'speak' : 'practice';
    return `/${exam}/${base}/${taskType}?set=${s.set_id}`;
  };

  if (error) {
    return (
      <AppShell>
        <p className="text-slate-500">{error}</p>
        <Link to={`/${exam}/practice`} className="cta mt-6">Back to practice</Link>
      </AppShell>
    );
  }

  const done = (sets || []).filter((s) => s.done).length;

  return (
    <AppShell>
      <Link to={`/${exam}/practice${section ? `?skill=${section}` : ''}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="picker-back">
        <ArrowLeft className="h-4 w-4" /> Practice
      </Link>
      <p className="mono-stat mt-6">{exam} · {section || 'practice'}</p>
      <h1 className="h-display mt-2 text-3xl">{unit?.title || prettyTask(taskType)}</h1>
      <div className="mt-2 flex items-baseline gap-4">
        {sets && <p className="text-sm text-slate-500">{done} of {sets.length} completed</p>}
        {unit && (
          <Link to={`/${exam}/lessons/${unit.unit_id}`} className="text-sm font-medium text-indigo-600 hover:text-indigo-800" data-testid="picker-strategy">
            Read the strategy first
          </Link>
        )}
      </div>

      {sets && sets.length > 0 && (() => {
        const nextOpen = sets.find((s) => !s.done && !s.locked) || sets.find((s) => !s.locked);
        if (!nextOpen) return null;
        return (
          <Link to={setPath(nextOpen)} className="cta mt-5 inline-flex" data-testid="picker-start">
            {done > 0 && done < sets.length ? 'Continue practice' : 'Start practice'} <ArrowRight className="h-4 w-4" />
          </Link>
        );
      })()}

      {!sets ? (
        <p className="mt-10 text-slate-400">Loading…</p>
      ) : (
        <div className="mt-8 max-w-3xl border border-slate-200 bg-white">
          {sets.map((s, i) => (
            <Link
              key={s.set_id}
              to={setPath(s)}
              data-testid={`set-${s.set_id}`}
              className={cn('group flex items-center gap-4 px-5 py-4 transition-colors hover:bg-slate-50', i > 0 && 'border-t border-slate-100')}
            >
              {(s.done || s.locked)
                ? <CheckCircle2 className="h-[18px] w-[18px] shrink-0 text-emerald-500" />
                : <Circle className="h-[18px] w-[18px] shrink-0 text-slate-200" />}
              <span className="w-7 shrink-0 text-xs font-medium tabular-nums text-slate-300">{String(i + 1).padStart(2, '0')}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-display text-[15px] font-semibold text-slate-900 group-hover:text-indigo-700">
                  {s.title || s.set_id}
                </span>
                <span className="text-xs text-slate-400">
                  {s.n_questions != null ? `${s.n_questions} question${s.n_questions === 1 ? '' : 's'}` : ''}
                  {s.locked
                    ? ' · tap to review'
                    : (s.attempts > 0
                        ? ` · ${s.attempts} attempt${s.attempts === 1 ? '' : 's'}${s.best_pct != null ? ` · best ${s.best_pct}%` : ''}`
                        : '')}
                </span>
              </span>
              {s.best_pct != null && (
                <span className={cn('h-display w-12 shrink-0 text-right text-lg tabular-nums', s.best_pct >= 75 ? 'text-emerald-600' : 'text-indigo-600')}>
                  {s.best_pct}%
                </span>
              )}
              <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-indigo-600" />
            </Link>
          ))}
        </div>
      )}
    </AppShell>
  );
}
